import { useState } from "react";
import { CheckCircle2, ChevronDown, Clock, User } from "lucide-react";
import { cn } from "../lib/utils";
import { formatShortDate } from "../../utils/dateFormatters";
import type { AccountActionItem } from "../types";

// ---------------------------------------------------------------------------
// Priority badge
// ---------------------------------------------------------------------------

const priorityConfig = {
  high: {
    label: "High",
    className:
      "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  },
  medium: {
    label: "Med",
    className:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  },
  low: {
    label: "Low",
    className:
      "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  },
} as const;

function PriorityBadge({ priority }: { priority: string }) {
  const config =
    priorityConfig[priority as keyof typeof priorityConfig] ??
    priorityConfig.medium;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none",
        config.className,
      )}
    >
      {config.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Single action item card
// ---------------------------------------------------------------------------

function ActionItemCard({ item }: { item: AccountActionItem }) {
  const dateStr = item.visit_date ? formatShortDate(item.visit_date) : null;

  return (
    <div className="rounded-lg bg-surface-muted p-2.5">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-1.5">
            <p className="text-xs text-text-secondary leading-relaxed">
              {item.task}
            </p>
            <PriorityBadge priority={item.priority} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-text-muted">
            {item.owner && (
              <span className="inline-flex items-center gap-0.5">
                <User className="h-2.5 w-2.5" />
                {item.owner}
              </span>
            )}
            {item.deadline_mentioned && (
              <span className="inline-flex items-center gap-0.5">
                <Clock className="h-2.5 w-2.5" />
                {item.deadline_mentioned}
              </span>
            )}
            {dateStr && <span>{dateStr}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionItemsSection
// ---------------------------------------------------------------------------

interface ActionItemsSectionProps {
  items: AccountActionItem[];
  className?: string;
}

export function ActionItemsSection({
  items,
  className,
}: ActionItemsSectionProps) {
  const [showAll, setShowAll] = useState(false);
  const displayItems = showAll ? items : items.slice(0, 5);
  const hasMore = items.length > 5;

  return (
    <div className={cn("space-y-1", className)}>
      <h4 className="text-xs font-medium uppercase tracking-wider text-text-muted">
        Action Items
      </h4>
      <div className="space-y-1.5">
        {displayItems.map((item, idx) => (
          <ActionItemCard key={`${item.trip_report_id}-${idx}`} item={item} />
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="mt-1 flex items-center gap-1 text-xs text-link hover:text-link-hover"
        >
          {showAll ? "Show less" : `Show all ${items.length}`}
          <ChevronDown
            className={cn(
              "h-3 w-3 transition-transform",
              showAll && "rotate-180",
            )}
          />
        </button>
      )}
    </div>
  );
}
